import * as React from 'react';
import { AccessTokenContext, AccessTokenState } from '../AccessToken';
import { AccessTokenStorage } from '../Api/AccessTokenStorage';
import { IApiClient } from '../ApiClient/';
import { Handler as IApiHandler } from '../ApiClient/Client';
import { Context as ApiContext } from '../ApiHooks/Context';
import { IErrorHandler } from './ErrorHandler';
import { ApplicationError } from '../ApplicationError';
import { ErrorBoundary } from '../ErrorHandler';
import { ErrorLogger } from '../ErrorLogger';
import { Initial } from '../Result';
import { ErrorAlert } from '../UI/ErrorAlert';

// tslint:disable:interface-over-type-literal
export type ServiceFactory = {
	createAccessTokenStorage(cookieName: string, options: { domain?: string }): AccessTokenStorage;
	createApiClient(apiBaseUrl: string, apiHandler: IApiHandler): IApiClient;
	createApiHandler(accessTokenStorage: AccessTokenStorage): IApiHandler;
	createErrorHandler(logger: ErrorLogger): IErrorHandler;
	createPigeonClient(pigeonBaseUrl: string, pigeonApiHandler: IApiHandler): IApiClient;
	createPigeonHandler(accessToken: AccessTokenStorage, pigeonAuthProvider: string): IApiHandler;
};

export const Application: React.FunctionComponent<{
	apiBaseUrl: string;
	errorLogger: ErrorLogger;
	pigeonAuthProvider: string;
	pigeonBaseUrl: string;
	serviceFactory: ServiceFactory;
	suppressErrorHandler?: boolean;
}> = ({
	children,
	apiBaseUrl,
	errorLogger,
	pigeonAuthProvider,
	pigeonBaseUrl,
	suppressErrorHandler,
	serviceFactory,
}) => {
	const errorHandler = React.useMemo(() => serviceFactory.createErrorHandler(errorLogger), []);
	const accessTokenStorage = React.useMemo(() => serviceFactory.createAccessTokenStorage('accessToken', {}), []);
	const apiHandler = React.useMemo(() => serviceFactory.createApiHandler(accessTokenStorage), []);
	const apiClient = React.useMemo(() => serviceFactory.createApiClient(apiBaseUrl, apiHandler), [apiBaseUrl]);
	const pigeonHandler = React.useMemo(
		() => serviceFactory.createPigeonHandler(accessTokenStorage, pigeonAuthProvider),
		[pigeonAuthProvider]
	);
	const pigeonClient = React.useMemo(
		() => serviceFactory.createPigeonClient(pigeonBaseUrl, pigeonHandler),
		[pigeonBaseUrl]
	);

	const accessTokenState = React.useState<AccessTokenState>({
		accessToken: new Initial(),
		needsFetch: React.useRef(true),
		onChange: (token) => {
			if (token !== null) {
				accessTokenStorage.save(token.id, token.expires);
			} else {
				accessTokenStorage.remove();
			}
		},
		token: accessTokenStorage.load(),
	});

	const stateRef = React.useRef({ accessTokenState, location });
	stateRef.current = { accessTokenState, location };

	return (
		<ErrorBoundary
			fallback={() => <ErrorAlert>{ApplicationError.generalFailure}</ErrorAlert>}
			handler={
				!Boolean(suppressErrorHandler)
					? (error, errorInfo) => errorHandler.handle(error, errorInfo !== undefined ? { react: errorInfo } : undefined)
					: null
			}
		>
			<AccessTokenContext.Provider value={accessTokenState}>
				<ApiContext.Provider value={{ api: apiClient, pigeon: pigeonClient }}>{children}</ApiContext.Provider>
			</AccessTokenContext.Provider>
		</ErrorBoundary>
	);
};
